import React from 'react';
import { useOutletContext } from 'react-router-dom';
import { GraduationCap, Heart, Users } from 'lucide-react';
import { formatMaskedSocialSecurityLast4 } from '@triedge/financial-aid-domain';
import type { FinancialAidStaffDetailContext } from '../ApplicantDetail';
import { asArray, asRecord } from '../../utils/domainData';
import {
  firstNonEmptyRawText,
  readParentCollectedFinancialAidSnapshot,
  readParentFinancialAidRelationshipKey,
  readParentFinancialAidSnapshotKey,
  readParentSnapshotTextFallback,
  readSnapshotArray,
  readSnapshotRecord,
} from './staffFinancialAidRawSnapshot';

const readText = (value: unknown, defaultValue = ''): string => {
  const next = String(value ?? '').trim();
  return next || defaultValue;
};

const readName = (record: Record<string, unknown>): string => {
  const full = readText(record.fullName);
  if (full) return full;
  const combined = `${readText(record.firstName)} ${readText(record.lastName)}`.trim();
  return combined || 'Not provided';
};

const readContact = (record: Record<string, unknown>): string =>
  readText(record.cellPhone, readText(record.workPhone, readText(record.email, 'Not provided')));

const readSsn = (record: Record<string, unknown>): string => {
  return formatMaskedSocialSecurityLast4(record.socialSecurityLast4);
};

const headingForParent = (relationship: string, fallback: string): string => {
  if (/father/i.test(relationship)) return "FATHER'S INFORMATION";
  if (/mother/i.test(relationship)) return "MOTHER'S INFORMATION";
  return fallback;
};

const hasParentPresenceSignals = (record: Record<string, unknown>): boolean =>
  [
    record.fullName,
    record.firstName,
    record.lastName,
    record.email,
    record.cellPhone,
    record.workPhone,
    record.occupation,
    record.employer,
  ].some((value) => Boolean(readText(value)));

type StudentView = { name: string; nickname: string };
type FamilyTabContext = Omit<
  FinancialAidStaffDetailContext,
  'isCorrectionEditing' | 'draftSections' | 'updateCorrectionSection'
> & Partial<Pick<FinancialAidStaffDetailContext, 'isCorrectionEditing' | 'draftSections' | 'updateCorrectionSection'>>;

const hasOwnRecordKey = (value: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const readDraftText = (draft: Record<string, unknown>, key: string, fallback: unknown): string =>
  hasOwnRecordKey(draft, key) ? String(draft[key] ?? '') : readText(fallback);

const EditableField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">{label}</span>
    <input
      type="text"
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
      className="mt-1 w-full rounded-lg border border-[#D1D5DB] bg-white px-3 py-2 text-sm font-semibold text-[#111827] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
    />
  </label>
);

export const FamilyTab: React.FC = () => {
  const context = useOutletContext<FamilyTabContext>();
  const {
    review,
    layoutMode,
  } = context;
  const correctionModeActive = context.isCorrectionEditing ?? false;
  const isCorrectionEditing = false;
  const draftSections = context.draftSections ?? {};
  const updateCorrectionSection = (sectionKey: string, patch: unknown) => {
    if (typeof context.updateCorrectionSection === 'function') {
      context.updateCorrectionSection(sectionKey, patch);
      return;
    }
    if (correctionModeActive) {
      throw new Error('Financial aid correction context is unavailable.');
    }
  };
  const isCompactLayout = layoutMode === 'compact';

  if (!review) {
    return (
      <div className="text-sm text-[#6B7280]">
        Application data is syncing.
      </div>
    );
  }

  const application = asRecord(review.application as unknown);
  const sections = asRecord(application.sections);
  const familyProfile = asRecord(sections.familyInformation);
  const parent1 = asRecord(sections.parent1);
  const parent2 = asRecord(sections.parent2);
  const familyProfileDraft = asRecord(draftSections.familyInformation);
  const parent1Draft = asRecord(draftSections.parent1);
  const parent2Draft = asRecord(draftSections.parent2);
  const nextFamilyProfile = (patch: Record<string, unknown>) =>
    updateCorrectionSection('familyInformation', patch);
  const nextParent1 = (patch: Record<string, unknown>) =>
    updateCorrectionSection('parent1', patch);
  const nextParent2 = (patch: Record<string, unknown>) =>
    updateCorrectionSection('parent2', patch);
  const rawSnapshot = readParentCollectedFinancialAidSnapshot(review);
  const rawFather = readSnapshotRecord(rawSnapshot, 'father');
  const rawMother = readSnapshotRecord(rawSnapshot, 'mother');

  const studentsFromReview = asArray<Record<string, unknown>>(review.students);
  const studentsFromSections = asArray<Record<string, unknown>>(sections.ystStudents);
  const studentSource = studentsFromReview.length > 0 ? studentsFromReview : studentsFromSections;
  const students: StudentView[] = studentSource.map((student, idx) => ({
    name: readText(
      `${readText(student.firstName)} ${readText(student.lastName)}`.trim(),
      readText(student.name, `Student ${idx + 1}`)
    ),
    nickname: readText(student.preferredName, readText(student.nickname, '')),
  }));

  const normalizedOtherTuition = asRecord(sections.otherTuition);
  const otherHouseholdChildren = [
    ...asArray<unknown>(familyProfile.otherHouseholdChildren)
      .map((entry) => readText(entry)),
    ...asArray<Record<string, unknown>>(normalizedOtherTuition.entries)
      .map((entry) => readText(entry.studentName, readText(entry.name))),
    ...readSnapshotArray(rawSnapshot, 'otherChildren')
      .map((entry) => firstNonEmptyRawText(entry.name, entry.studentName)),
  ]
    .map((entry) => readText(entry))
    .filter((entry, index, entries) => Boolean(entry) && entries.indexOf(entry) === index);

  const parent1Relationship = readText(parent1.relationship);
  const parent2Relationship = readText(parent2.relationship);
  const canEditParent2 = Boolean(parent2Relationship);
  const parent1SnapshotKey = readParentFinancialAidSnapshotKey(parent1Relationship, 'father');
  const parent2RelationshipKey = readParentFinancialAidRelationshipKey(parent2Relationship);
  const fallbackParent2SnapshotKey = hasParentPresenceSignals(parent2)
    ? parent1SnapshotKey === 'father' ? 'mother' : 'father'
    : null;
  const parent2SnapshotKey = parent2RelationshipKey
    ? parent2RelationshipKey === parent1SnapshotKey ? null : parent2RelationshipKey
    : fallbackParent2SnapshotKey;
  const rawParent1 = readSnapshotRecord(rawSnapshot, parent1SnapshotKey);
  const rawParent2 = parent2SnapshotKey ? readSnapshotRecord(rawSnapshot, parent2SnapshotKey) : {};
  const parent1Occupation = readParentSnapshotTextFallback(parent1, rawParent1, 'occupation', 'Not provided');
  const parent1Employer = readParentSnapshotTextFallback(parent1, rawParent1, 'employer', 'Not provided');
  const parent2Occupation = readParentSnapshotTextFallback(parent2, rawParent2, 'occupation', 'Not provided');
  const parent2Employer = readParentSnapshotTextFallback(parent2, rawParent2, 'employer', 'Not provided');
  const fatherShul = firstNonEmptyRawText(rawFather.shul);
  const motherShul = firstNonEmptyRawText(rawMother.shul);
  const fatherRabbi = firstNonEmptyRawText(rawFather.rabbi);
  const motherRabbi = firstNonEmptyRawText(rawMother.rabbi);
  // Distinct father/mother religious-affiliation values must stay distinct in
  // staff review instead of collapsing to the father's entry (INT-620).
  const hasDistinctShul = Boolean(fatherShul && motherShul && fatherShul !== motherShul);
  const hasDistinctRabbi = Boolean(fatherRabbi && motherRabbi && fatherRabbi !== motherRabbi);
  const rawSynagogue = fatherShul || motherShul;
  const rawRabbi = fatherRabbi || motherRabbi;
  const synagogue = readText(
    familyProfile.synagogue,
    readText(familyProfile.affiliation, rawSynagogue || 'Not provided')
  );
  const rabbi = readText(familyProfile.rabbi, rawRabbi || 'Not provided');

  return (
    <div
      data-testid="financial-aid-family-profile-tab"
      data-layout-mode={layoutMode}
      className="space-y-8 max-w-5xl"
    >
      <div className="bg-white rounded-xl border" style={{ borderColor: '#E5E7EB' }}>
        <div className="flex items-center gap-3 p-6 border-b" style={{ borderColor: '#F3F4F6' }}>
          <Users size={18} style={{ color: '#404F4D' }} />
          <h2 className="text-lg font-semibold text-[#111827]">Parents Information</h2>
        </div>
        <div className={`flex ${isCompactLayout ? 'flex-col' : 'flex-row'}`}>
          <div className={`flex-1 p-6 ${isCompactLayout ? 'border-b' : 'border-r'}`} style={{ borderColor: '#F3F4F6' }}>
            <h3 className="text-xs font-semibold tracking-wider uppercase mb-6" style={{ color: '#404F4D' }}>
              {headingForParent(parent1Relationship, 'PARENT / GUARDIAN 1')}
            </h3>
            <div className="space-y-5">
              <div>
                <p className="text-xs uppercase mb-1" style={{ color: '#9CA3AF' }}>FULL NAME</p>
                {isCorrectionEditing ? (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <EditableField
                      label="First"
                      value={readDraftText(parent1Draft, 'firstName', parent1.firstName)}
                      onChange={(value) => nextParent1({ firstName: value })}
                    />
                    <EditableField
                      label="Last"
                      value={readDraftText(parent1Draft, 'lastName', parent1.lastName)}
                      onChange={(value) => nextParent1({ lastName: value })}
                    />
                  </div>
                ) : (
                  <p className="text-sm font-medium text-[#111827]">{readName(parent1)}</p>
                )}
              </div>
              <div>
                <p className="text-xs uppercase mb-1" style={{ color: '#9CA3AF' }}>OCCUPATION</p>
                {isCorrectionEditing ? (
                  <EditableField
                    label="Occupation"
                    value={readDraftText(parent1Draft, 'occupation', parent1Occupation)}
                    onChange={(value) => nextParent1({ occupation: value })}
                  />
                ) : (
                  <p className="text-sm font-medium text-[#111827]">{parent1Occupation}</p>
                )}
              </div>
              <div>
                <p className="text-xs uppercase mb-1" style={{ color: '#9CA3AF' }}>EMPLOYER</p>
                {isCorrectionEditing ? (
                  <EditableField
                    label="Employer"
                    value={readDraftText(parent1Draft, 'employer', parent1Employer)}
                    onChange={(value) => nextParent1({ employer: value })}
                  />
                ) : (
                  <p className="text-sm font-medium text-[#111827]">{parent1Employer}</p>
                )}
              </div>
              <div>
                <p className="text-xs uppercase mb-1" style={{ color: '#9CA3AF' }}>CONTACT</p>
                {isCorrectionEditing ? (
                  <div className="grid grid-cols-1 gap-2">
                    <EditableField
                      label="Email"
                      value={readDraftText(parent1Draft, 'email', parent1.email)}
                      onChange={(value) => nextParent1({ email: value })}
                    />
                    <EditableField
                      label="Cell"
                      value={readDraftText(parent1Draft, 'cellPhone', parent1.cellPhone)}
                      onChange={(value) => nextParent1({ cellPhone: value })}
                    />
                  </div>
                ) : (
                  <p className="text-sm font-medium text-[#111827]">{readContact(parent1)}</p>
                )}
              </div>
              <div>
                <p className="text-xs uppercase mb-1" style={{ color: '#9CA3AF' }}>SSN</p>
                <p className="text-sm font-medium text-[#111827]">{readSsn(parent1)}</p>
              </div>
            </div>
          </div>

          <div className="flex-1 p-6">
            <h3 className="text-xs font-semibold tracking-wider uppercase mb-6" style={{ color: '#404F4D' }}>
              {headingForParent(parent2Relationship, "MOTHER'S INFORMATION")}
            </h3>
            <div className="space-y-5">
              <div>
                <p className="text-xs uppercase mb-1" style={{ color: '#9CA3AF' }}>FULL NAME</p>
                {isCorrectionEditing && canEditParent2 ? (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <EditableField
                      label="First"
                      value={readDraftText(parent2Draft, 'firstName', parent2.firstName)}
                      onChange={(value) => nextParent2({ firstName: value })}
                    />
                    <EditableField
                      label="Last"
                      value={readDraftText(parent2Draft, 'lastName', parent2.lastName)}
                      onChange={(value) => nextParent2({ lastName: value })}
                    />
                  </div>
                ) : (
                  <p className="text-sm font-medium text-[#111827]">{readName(parent2)}</p>
                )}
              </div>
              <div>
                <p className="text-xs uppercase mb-1" style={{ color: '#9CA3AF' }}>OCCUPATION</p>
                {isCorrectionEditing && canEditParent2 ? (
                  <EditableField
                    label="Occupation"
                    value={readDraftText(parent2Draft, 'occupation', parent2Occupation)}
                    onChange={(value) => nextParent2({ occupation: value })}
                  />
                ) : (
                  <p className="text-sm font-medium text-[#111827]">{parent2Occupation}</p>
                )}
              </div>
              <div>
                <p className="text-xs uppercase mb-1" style={{ color: '#9CA3AF' }}>EMPLOYER</p>
                {isCorrectionEditing && canEditParent2 ? (
                  <EditableField
                    label="Employer"
                    value={readDraftText(parent2Draft, 'employer', parent2Employer)}
                    onChange={(value) => nextParent2({ employer: value })}
                  />
                ) : (
                  <p className="text-sm font-medium text-[#111827]">{parent2Employer}</p>
                )}
              </div>
              <div>
                <p className="text-xs uppercase mb-1" style={{ color: '#9CA3AF' }}>CONTACT</p>
                {isCorrectionEditing && canEditParent2 ? (
                  <div className="grid grid-cols-1 gap-2">
                    <EditableField
                      label="Email"
                      value={readDraftText(parent2Draft, 'email', parent2.email)}
                      onChange={(value) => nextParent2({ email: value })}
                    />
                    <EditableField
                      label="Cell"
                      value={readDraftText(parent2Draft, 'cellPhone', parent2.cellPhone)}
                      onChange={(value) => nextParent2({ cellPhone: value })}
                    />
                  </div>
                ) : (
                  <p className="text-sm font-medium text-[#111827]">{readContact(parent2)}</p>
                )}
              </div>
              <div>
                <p className="text-xs uppercase mb-1" style={{ color: '#9CA3AF' }}>SSN</p>
                <p className="text-sm font-medium text-[#111827]">{readSsn(parent2)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E5E7EB' }}>
        <div className="flex items-center gap-3 mb-6">
          <Heart size={18} style={{ color: '#404F4D' }} />
          <h2 className="text-lg font-semibold text-[#111827]">Religious Affiliation</h2>
        </div>
        <div className={`grid ${isCompactLayout ? 'grid-cols-1' : 'grid-cols-2'} gap-6`}>
          <div>
            <p className="text-xs uppercase mb-1" style={{ color: '#9CA3AF' }}>SYNAGOGUE</p>
            {isCorrectionEditing ? (
              <EditableField
                label="Synagogue"
                value={readDraftText(familyProfileDraft, 'synagogue', synagogue)}
                onChange={(value) => nextFamilyProfile({ synagogue: value })}
              />
            ) : hasDistinctShul ? (
              <div className="space-y-1">
                <p className="text-sm font-medium text-[#111827]">Father: {fatherShul}</p>
                <p className="text-sm font-medium text-[#111827]">Mother: {motherShul}</p>
              </div>
            ) : (
              <p className="text-sm font-medium text-[#111827]">{synagogue}</p>
            )}
          </div>
          <div>
            <p className="text-xs uppercase mb-1" style={{ color: '#9CA3AF' }}>RABBI</p>
            {isCorrectionEditing ? (
              <EditableField
                label="Rabbi"
                value={readDraftText(familyProfileDraft, 'rabbi', rabbi)}
                onChange={(value) => nextFamilyProfile({ rabbi: value })}
              />
            ) : hasDistinctRabbi ? (
              <div className="space-y-1">
                <p className="text-sm font-medium text-[#111827]">Father: {fatherRabbi}</p>
                <p className="text-sm font-medium text-[#111827]">Mother: {motherRabbi}</p>
              </div>
            ) : (
              <p className="text-sm font-medium text-[#111827]">{rabbi}</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E5E7EB' }}>
        <div className="flex items-center gap-3 mb-6">
          <GraduationCap size={18} style={{ color: '#404F4D' }} />
          <h2 className="text-lg font-semibold text-[#111827]">Students Enrolled</h2>
        </div>
        <div className="space-y-4">
          {students.length === 0 ? (
            <p className="text-sm italic" style={{ color: '#9CA3AF' }}>None reported.</p>
          ) : (
            students.map((student, index) => (
              <div
                key={`${student.name}-${index}`}
                className={`border rounded-lg p-4 gap-3 ${isCompactLayout ? 'flex flex-col items-start' : 'flex justify-between items-center'}`}
                style={{ borderColor: '#E5E7EB' }}
              >
                <span className="text-sm font-medium text-[#111827]">{student.name}</span>
                <span className="text-sm text-[#6B7280]">
                  {student.nickname ? `NICKNAME: ${student.nickname}` : 'NICKNAME: —'}
                </span>
              </div>
            ))
          )}

          <div className="pt-4">
            <p className="text-xs uppercase mb-2" style={{ color: '#9CA3AF' }}>OTHER HOUSEHOLD CHILDREN</p>
            {otherHouseholdChildren.length === 0 ? (
              <p className="text-sm italic" style={{ color: '#9CA3AF' }}>None reported.</p>
            ) : (
              <div className="space-y-1">
                {otherHouseholdChildren.map((entry) => (
                  <p key={entry} className="text-sm text-[#111827]">{entry}</p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
